import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

export const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api')) return `${BACKEND_URL}${url}`;
  return url;
};

const BrandingContext = createContext(null);

export const useBranding = () => useContext(BrandingContext);

const DEFAULT_BRANDING = {
  site_name: 'Estadía 365',
  tagline: 'Tu hogar lejos de casa',
  hero_title: 'Encontra tu lugar ideal',
  hero_subtitle: 'Descubri alojamientos unicos en Paraguay, Argentina y Brasil',
  hero_description: 'Mas de {properties} propiedades unicas en {cities} ciudades de Paraguay. Desde hoteles boutique hasta cabañas rodeadas de naturaleza.',
  logo_url: '',
  favicon_url: '',
  colors: {
    primary: '#264639',
    primary_hover: '#1C352B',
    secondary: '#C36F4D',
    accent: '#DCE9E2',
    background: '#F5F4F0',
    surface: '#FFFFFF',
    text_primary: '#1C1917',
    text_secondary: '#57534E',
    text_muted: '#78716C',
    border: '#E7E5E4',
  },
  fonts: {
    heading: 'Outfit',
    body: 'Inter',
  },
  social: {
    whatsapp: '',
    instagram: '',
    facebook: '',
    twitter: '',
    tiktok: '',
  },
  contact: {
    phone: '',
    email: '',
    address: '',
  },
  footer_text: 'Estadía 365. Tu hogar lejos de casa.',
  meta_description: 'Alojamientos unicos en Paraguay, Argentina y Brasil.',
  features_label: 'Por que elegirnos',
  features_title: 'Todo lo que necesitas para elegir mejor tu estadia',
  features: [
    { icon: 'building', title: 'Mas de 665 alojamientos', description: 'Hoteles, cabañas, departamentos y mas opciones en Paraguay, Argentina y Brasil.' },
    { icon: 'headset', title: 'Atencion rapida', description: 'Te ayudamos a encontrar la opcion ideal para tu viaje, con acompanamiento personalizado.' },
    { icon: 'shield-check', title: 'Reserva confiable', description: 'Proceso claro, alojamientos verificados y gestión profesional en cada estadia.' },
    { icon: 'lock', title: 'Reserva protegida', description: 'Pago seguro, respaldo ante inconvenientes y mayor tranquilidad durante todo el proceso.' },
  ],
  destinations_label: 'Destinos',
  destinations_title: 'Explora nuestros paises',
  destinations: [
    { code: 'PY', name: 'Paraguay', count: 580, cities: 'Encarnacion, Asuncion, Ciudad del Este', color: '#DCE9E2' },
    { code: 'AR', name: 'Argentina', count: 75, cities: 'Posadas, Buenos Aires, Obera', color: '#F5E4DE' },
    { code: 'BR', name: 'Brasil', count: 10, cities: 'Foz do Iguacu', color: '#E5EDFF' },
  ],
  featured_label: 'Destacados',
  featured_title: 'Propiedades populares',
  testimonials_label: 'Testimonios',
  testimonials_title: 'Lo que dicen nuestros huéspedes',
  cta_title: 'Tienes una propiedad?',
  cta_subtitle: 'Unite a nuestra plataforma y llega a miles de viajeros.',
  cta_button: 'Registrar mi propiedad',
  about_us: {
    hero_title: 'Estadía 365 conecta alojamientos con huéspedes durante todo el año',
    hero_description: 'Somos una marca especializada en alojamientos temporarios, vacacionales y turísticos.',
    story: '',
    story_2: '',
    story_3: '',
    purpose_title: 'Convertir alojamientos en experiencias confiables',
    purpose_description: '',
    services: [
      { title: 'Para huéspedes', description: '' },
      { title: 'Para propietarios y hoteles', description: '' },
      { title: 'Para inversores', description: '' },
    ],
  },
  faq: {
    title: 'Información para reservar',
    description: '',
    questions: [],
  },
};

function hexToHSL(hex) {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function lightenColor(hex, amount) {
  hex = hex.replace('#', '');
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  r = Math.min(255, Math.round(r + (255 - r) * amount));
  g = Math.min(255, Math.round(g + (255 - g) * amount));
  b = Math.min(255, Math.round(b + (255 - b) * amount));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

export function applyBrandingToDOM(branding) {
  const root = document.documentElement;
  const c = branding.colors || {};

  const isValidHex = (v) => typeof v === 'string' && /^#[0-9A-Fa-f]{3,8}$/.test(v);

  // Apply raw hex colors as CSS custom properties for inline styles
  Object.entries(c).forEach(([key, value]) => {
    if (value) {
      root.style.setProperty(`--brand-${key.replace(/_/g, '-')}`, value);
    }
  });

  // Apply light variants for dark mode readability
  if (isValidHex(c.primary)) {
    root.style.setProperty('--brand-primary-light', lightenColor(c.primary, 0.4));
  }
  if (c.text_primary) {
    root.style.setProperty('--brand-text-primary-dark', '#F5F4F0');
  }
  if (c.text_secondary) {
    root.style.setProperty('--brand-text-secondary-dark', '#A8A29E');
  }

  // Apply HSL versions for Shadcn/Tailwind compatibility (with validation)
  if (isValidHex(c.primary)) {
    root.style.setProperty('--primary', hexToHSL(c.primary));
    root.style.setProperty('--primary-foreground', '0 0% 100%');
  }
  if (isValidHex(c.secondary)) root.style.setProperty('--secondary', hexToHSL(c.secondary));
  if (isValidHex(c.accent)) root.style.setProperty('--accent', hexToHSL(c.accent));
  if (isValidHex(c.background)) root.style.setProperty('--background', hexToHSL(c.background));
  if (isValidHex(c.surface)) root.style.setProperty('--card', hexToHSL(c.surface));
  if (isValidHex(c.border)) root.style.setProperty('--border', hexToHSL(c.border));
  if (isValidHex(c.text_primary)) root.style.setProperty('--foreground', hexToHSL(c.text_primary));
  if (isValidHex(c.text_muted)) root.style.setProperty('--muted-foreground', hexToHSL(c.text_muted));

  // Fonts
  const fonts = branding.fonts || {};
  if (fonts.heading) root.style.setProperty('--font-heading', `'${fonts.heading}', sans-serif`);
  if (fonts.body) root.style.setProperty('--font-body', `'${fonts.body}', sans-serif`);

  // Load Google Fonts dynamically
  const fontsToLoad = [fonts.heading, fonts.body].filter(Boolean);
  fontsToLoad.forEach(fontName => {
    const id = `gfont-${fontName.replace(/\s+/g, '-').toLowerCase()}`;
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@300;400;500;600;700&display=swap`;
      document.head.appendChild(link);
    }
  });

  // Update document title
  if (branding.site_name && !window.location.pathname.startsWith('/qr')) {
    document.title = `${branding.site_name} | ${branding.tagline || 'Alojamientos'}`;
  }

  // Update favicon
  if (branding.favicon_url) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    const faviconUrl = branding.favicon_url.startsWith('/api') 
      ? `${BACKEND_URL}${branding.favicon_url}` 
      : branding.favicon_url;
    link.href = faviconUrl;
  }

  // Update meta description
  if (branding.meta_description) {
    let meta = document.querySelector("meta[name='description']");
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = branding.meta_description;
  }
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loaded, setLoaded] = useState(false);

  const fetchBranding = useCallback(async () => {
    try {
      const res = await api.get('/settings/branding');
      const b = res.data.branding || DEFAULT_BRANDING;
      setBranding(b);
      applyBrandingToDOM(b);
    } catch {
      applyBrandingToDOM(DEFAULT_BRANDING);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  const refreshBranding = useCallback(async () => {
    await fetchBranding();
  }, [fetchBranding]);

  const value = useMemo(() => ({ branding, loaded, refreshBranding }), [branding, loaded, refreshBranding]);

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}
